import { Link } from 'react-router-dom';

import { AnchorHeading } from '../components/AnchorHeading';
import { Figure, Flow } from '../components/Diagram';
import { UiPath } from '../components/UiPath';
import { useDocsPage } from '../hooks/useDocsPage';

export function SupportInboxGuide() {
  useDocsPage('Work the support inbox — Revvly Docs');
  return (
    <div className="tn-page">
      <nav className="dp-crumbs" aria-label="Breadcrumb">
        <Link to="/docs/guides">Guides</Link>
        <span aria-hidden="true">›</span>
        <span className="dp-crumb-current">Work the support inbox</span>
      </nav>
      <header className="dp-header">
        <span className="rv-eyebrow-mini">Guide</span>
        <h1>
          Work the <em>support inbox</em>.
        </h1>
        <p className="dp-oneliner">
          Where requests from your customers arrive, how to find yours, answer it, hand it on and
          close it, and how to read the numbers on the Metrics page. Written for the people who work
          tickets every day.
        </p>
      </header>

      <section className="dp-section">
        <AnchorHeading id="access">Who can use it</AnchorHeading>
        <UiPath
          steps={['Support', 'Inbox']}
          note="Support has four pages in the sidebar: Inbox, End users, Knowledge and Metrics. If the section is missing, Support is not switched on for your workspace."
        />
        <p className="dp-section-sub">
          Everyone who is an active member of your workspace is an agent: they can read every
          ticket, reply, take, assign and close. Changing how Support is set up (ticket types,
          roles, AI, webhooks) is for admins and owners only; everyone else sees those pages
          read-only. To add a colleague, see{' '}
          <Link to="/docs/guides/add-user">Add a team member</Link>.
        </p>
      </section>

      <section className="dp-section">
        <AnchorHeading id="views">Views, filters and search</AnchorHeading>
        <p className="dp-section-sub">
          The column on the left lists the views, each with a count. A view is a starting list; the
          newest activity is always at the top.
        </p>
        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">Unassigned</div>
            <div className="dp-field-v">
              Open tickets nobody owns. New tickets, and tickets the AI has handed over, wait here.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Mine</div>
            <div className="dp-field-v">Tickets assigned to you.</div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">All open</div>
            <div className="dp-field-v">Every ticket that is not closed. This is the default.</div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Waiting on client</div>
            <div className="dp-field-v">
              Tickets in a status of the Pending category, such as Answered or Resolved, where the
              next move is the customer&rsquo;s.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Closed</div>
            <div className="dp-field-v">Closed tickets. It has no count.</div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Untitled</div>
            <div className="dp-field-v">
              Tickets that have no title yet. The AI names most of them; see{' '}
              <Link to="/docs/guides/support-ai#titles">automatic titles</Link>.
            </div>
          </div>
        </div>
        <p className="dp-section-sub">
          <strong>Filters.</strong> Select Filter above the list to narrow the current view by
          ticket type, team, status (Open, In progress, Pending, Closed), client, the role the
          customer held when they wrote in, and source (where the request came from, for example the
          page of your product). Each filter appears as a chip you can remove. There is no filter
          for the assignee: use Mine and Unassigned for that.
        </p>
        <p className="dp-section-sub">
          <strong>Search.</strong> The box above the list takes at least two characters and looks at
          ticket numbers (for example <span className="api-inline">AA-42</span>), titles and message
          text, internal notes included. It searches all your tickets, open and closed, and each
          ticket appears once with the part that matched. Clear the box to go back to the view.
        </p>
        <p className="dp-section-sub">
          <strong>Keyboard.</strong> <span className="api-inline">j</span> and{' '}
          <span className="api-inline">k</span> move down and up the list, and{' '}
          <span className="api-inline">Enter</span> opens the selected ticket. On a wide screen the
          right-hand pane previews the conversation. The list updates on its own when something
          changes.
        </p>
      </section>

      <section className="dp-section">
        <AnchorHeading id="log">Log a ticket for a client</AnchorHeading>
        <UiPath
          steps={['Support', 'Inbox', 'New ticket']}
          note="Any agent. For a request that did not come through the widget."
        />
        <p className="dp-section-sub">
          When a client asks for something another way, on a call or by email, log it so it is
          tracked like any other ticket. Choose the <strong>Type</strong> and give it a{' '}
          <strong>Title</strong>, then name the <strong>Person</strong> from People, the{' '}
          <strong>Client</strong>, or both. A person who belongs to a client is filed under that
          client, so the client picker is fixed to theirs. Set the <strong>Priority</strong> and the{' '}
          <strong>Assignee</strong>, which is you unless you change it, and write{' '}
          <strong>What the client asked</strong>.
        </p>
        <p className="dp-section-sub">
          That text is saved as an <strong>internal note</strong>. The AI does not answer it, no
          email goes out, and the client does not see the ticket in their widget. It becomes the
          client&rsquo;s only when someone replies to them in public; until then, nothing about it
          reaches them, including live updates, emails, webhooks and stage updates. An assignee is
          told once, that it is theirs.
        </p>
      </section>

      <section className="dp-section">
        <AnchorHeading id="ticket">Open a ticket</AnchorHeading>
        <p className="dp-section-sub">
          The conversation is on the left, with the composer underneath. Under the title, chips show
          the ticket&rsquo;s status, priority, assignee, type, team and tags; select a chip to
          change it, and it saves at once. On a ticket that is not closed, the header also has an{' '}
          <strong>AI replies too</strong> switch that keeps the AI answering the customer beside
          your team; see <Link to="/docs/guides/support-ai#handover">how handover works</Link>.
          Select the title to edit it; clearing it makes the ticket Untitled again. On a narrow
          screen, Details opens the side panel.
        </p>
        <p className="dp-section-sub">The side of the page holds:</p>
        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">Client</div>
            <div className="dp-field-v">
              Who wrote in (name, role, plan and any attributes your system sent about them), the
              client they belong to, and that client&rsquo;s other open tickets. It says &ldquo;No
              client linked&rdquo; when the person is not matched to a client.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">ClickUp</div>
            <div className="dp-field-v">
              Tasks linked to this ticket, when ClickUp is connected. See{' '}
              <Link to="/docs/guides/support-setup#clickup">Set up Support</Link>.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Documents sent</div>
            <div className="dp-field-v">
              Library documents you sent as links on this ticket, and whether the customer opened
              them. Only agents see this. It appears once a link has been sent.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Details</div>
            <div className="dp-field-v">
              Channel, where it was opened from, the customer&rsquo;s role, when it was created, how
              long the first reply took, last activity, when it was closed and how, and whether the
              AI is answering or has handed over.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">History</div>
            <div className="dp-field-v">
              Every change, with who made it. Internal events are included; an eye marks the ones
              the customer can also see. You can filter which events are shown.
            </div>
          </div>
        </div>
      </section>

      <section className="dp-section">
        <AnchorHeading id="reply">Reply to the customer, or write a note</AnchorHeading>
        <p className="dp-section-sub">
          The composer has two modes, and the whole box changes colour in note mode so you cannot
          mistake one for the other.
        </p>
        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">Reply</div>
            <div className="dp-field-v">
              Visible to the customer. The button says Send reply. Customers who have not read it
              after a delay are also emailed the reply; see{' '}
              <Link to="/docs/guides/support-setup#notifications">notifications</Link>.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Internal note</div>
            <div className="dp-field-v">
              Only agents see it, marked &ldquo;Internal note, not visible to the client&rdquo;. The
              button says Add internal note.
            </div>
          </div>
        </div>
        <p className="dp-section-sub">
          Send with Ctrl+Enter (Cmd+Enter on a Mac). If a send fails, press it again: the message is
          only saved once.
        </p>
        <p className="dp-section-sub">
          <strong>Mentions.</strong> In an internal note, type <span className="api-inline">@</span>{' '}
          and pick a colleague. They get a notification (in the app and by email, unless they turned
          those off) saying they were mentioned. A public reply never carries a mention, because the
          customer would see it.
        </p>
        <p className="dp-section-sub">
          <strong>Attachments.</strong> The paperclip adds files to a reply or a note: up to 10 per
          message, up to 20 MB each, images (PNG, JPEG, GIF, WebP), PDF or plain text. Anything else
          is refused before it uploads. Every upload is checked before it can be sent: the real
          contents must match the file type, plain text may not contain markup, and where malware
          scanning is set up the file is scanned. If the scanner cannot be reached the upload waits
          and you can try again, rather than letting an unchecked file through. Customers see images
          as a re-encoded preview and other files as downloads, and customers&rsquo; own uploads go
          through the same checks.
        </p>
        <p className="dp-section-sub">
          <strong>Library documents.</strong> On a public reply, the library button adds up to five
          documents from your Document library as tracked links. See{' '}
          <Link to="/docs/guides/document-library">Reuse documents</Link>.
        </p>
      </section>

      <section className="dp-section">
        <AnchorHeading id="own">Take, assign and prioritise</AnchorHeading>
        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">Take it</div>
            <div className="dp-field-v">
              On an unassigned open ticket, Take it assigns the ticket to you. If two people take
              the same ticket at the same moment, one wins and the other is told who has it.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Assignee</div>
            <div className="dp-field-v">
              Pick any active member of the workspace, or Unassigned. The new assignee is notified.
              Once a ticket has an assignee, the AI stops answering it.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Team</div>
            <div className="dp-field-v">
              Optional, and separate from the assignee: a team can hold tickets nobody has taken
              yet. Teams are managed under Org Settings, Teams. Nothing assigns tickets
              automatically.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Status</div>
            <div className="dp-field-v">
              The list shows the statuses of this ticket&rsquo;s workflow, grouped by category.
              Closed statuses are greyed with &ldquo;Use Close ticket&rdquo;, because closing
              carries a resolution.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Priority</div>
            <div className="dp-field-v">Low, Normal, High or Urgent.</div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Type</div>
            <div className="dp-field-v">
              Changing the type moves the ticket to the matching status of the new type&rsquo;s
              workflow, or to the equivalent status of the same category. It never closes or reopens
              a ticket.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Tags</div>
            <div className="dp-field-v">Free-form labels, up to 40 characters each.</div>
          </div>
        </div>
        <div className="api-note">
          <span className="api-note-icon">i</span>
          <span>
            If a colleague changes the ticket while you are on it, a notice says what changed. An
            edit made against an out-of-date copy is refused rather than overwriting theirs; reload
            and make it again.
          </span>
        </div>
      </section>

      <section className="dp-section">
        <AnchorHeading id="close">Close, reopen and auto-close</AnchorHeading>
        <p className="dp-section-sub">
          <strong>Close ticket</strong> asks how it ended: Solved by agent, Duplicate or Won&rsquo;t
          fix. The customer is told it is closed and can reopen it. Three other outcomes appear on
          tickets but you cannot choose them: Solved by AI (the customer confirmed an AI answer),
          Closed by client (the customer closed it) and Closed automatically.
        </p>
        <p className="dp-section-sub">
          <strong>Reopen.</strong> A closed ticket shows Reopen instead of Close ticket, and its
          properties are read-only until you use it. It returns to the first status of its
          type&rsquo;s workflow. A customer who writes again on a closed ticket reopens it
          themselves, and the assignee is notified of the reply.
        </p>
        <p className="dp-section-sub">
          <strong>Auto-close.</strong> When it is switched on, a ticket sitting in a Pending status
          (Answered, Resolved) is closed as Closed automatically once it has waited that long
          without a reply from the customer. The default is 48 hours and the range is 1 to 720. It
          is checked every five minutes. A customer reply stops the clock; it does not move the
          status, so move the ticket on yourself if it needs work.
        </p>
        <UiPath
          steps={['Org Settings', 'Support', 'General', 'Auto-close']}
          note="Admins and owners. Switch it on, set the hours, then save."
        />
        <Figure
          title="How a ticket gets closed"
          caption="Every close carries a resolution, and you can choose only three of the four ways it happens."
        >
          <Flow
            steps={[
              { title: 'New ticket', text: 'Waits in Unassigned until someone takes it.' },
              { title: 'Being worked', text: 'Take it or assign it, then reply or add notes.' },
              {
                title: 'Waiting on the customer',
                text: 'A Pending status, such as Answered or Resolved.',
                tone: 'muted',
              },
              {
                fork: [
                  {
                    title: 'You close it',
                    text: 'Solved by agent, Duplicate or Won\u2019t fix.',
                    tone: 'accent',
                  },
                  { title: 'Customer closes it', text: 'Closed by client.' },
                  { title: 'The AI solved it', text: 'Customer confirmed an AI answer.' },
                  {
                    title: 'Auto-close',
                    text: 'Closed automatically after the wait you set, if switched on.',
                    tone: 'warn',
                  },
                ],
              },
              {
                title: 'Closed',
                text: 'Reopen returns it to the first status of its workflow.',
                tone: 'muted',
              },
            ]}
          />
        </Figure>
        <p className="dp-section-sub">
          When a closed ticket has a <strong>Close summary</strong> at the top, it was written by
          the AI for agents only; see{' '}
          <Link to="/docs/guides/support-ai#titles">titles and summaries</Link>.
        </p>
      </section>

      <section className="dp-section">
        <AnchorHeading id="notifications">Notifications</AnchorHeading>
        <p className="dp-section-sub">
          The bell in the top bar lists what needs you: a new ticket, a ticket assigned to you, a
          reply on your ticket, a status change on your ticket, and a mention. You are never
          notified of your own actions. The bell links to Notification preferences, where you choose
          in-app and email for each kind; see{' '}
          <Link to="/docs/guides/support-setup#notifications">Set up Support</Link> for the
          defaults.
        </p>
      </section>

      <section className="dp-section">
        <AnchorHeading id="client-page">A client&rsquo;s tickets on the client page</AnchorHeading>
        <UiPath
          steps={['Clients', 'open a client', 'Support']}
          note="Shown when Support is on, for clients and not for prospects."
        />
        <p className="dp-section-sub">
          The Support tab lists that client&rsquo;s tickets, newest activity first, with a filter
          for All, Open, Pending, In progress or Closed. A closed ticket shows its close summary
          underneath. Selecting a row opens the ticket.
        </p>
      </section>

      <section className="dp-section">
        <AnchorHeading id="metrics">Read the Metrics page</AnchorHeading>
        <UiPath
          steps={['Support', 'Metrics']}
          note="Choose a period (7, 30 or 90 days, 12 months, or a custom range of up to a year). Days are UTC days."
        />
        <p className="dp-section-sub">The four tiles at the top cover the whole period.</p>
        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">Tickets created</div>
            <div className="dp-field-v">How many tickets were opened in the period.</div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Tickets closed</div>
            <div className="dp-field-v">
              How many times a ticket was closed in the period, whenever it was opened and however
              it was closed (by an agent, the customer, the AI or auto-close). A ticket that is
              closed, reopened and closed again counts twice.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Median first response</div>
            <div className="dp-field-v">
              For tickets created in the period that have had a reply: the middle value of the time
              from creation to the first public reply from an agent or the AI. Internal notes do not
              count, and neither does a progress update about linked work.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Median resolution</div>
            <div className="dp-field-v">
              For every close in the period: the middle value of the time from the ticket&rsquo;s
              creation to that close. A reopened ticket&rsquo;s second close is measured from the
              original creation.
            </div>
          </div>
        </div>
        <p className="dp-section-sub">
          A median is the middle value, so one very slow ticket does not drag it up the way an
          average would. Times are elapsed clock time, not working hours. A dash means there was
          nothing to measure.
        </p>
        <p className="dp-section-sub">
          Underneath, break the same numbers down by <strong>Type</strong>, <strong>Agent</strong>{' '}
          or <strong>Day</strong>. By agent uses the ticket&rsquo;s current assignee at the moment
          you look, not who wrote the reply, and groups unassigned tickets together. By day draws a
          chart of tickets created and closed per UTC day, with quiet days shown as zero. With no
          tickets in the period the page says &ldquo;Not enough data yet&rdquo;; pick a longer
          range.
        </p>
      </section>

      <section className="dp-section">
        <AnchorHeading id="not-built">Not built yet</AnchorHeading>
        <ul className="dp-connects">
          <li>Automatic assignment rules: tickets are taken or assigned by people.</li>
          <li>
            A notification when the AI hands a ticket over. Watch the Unassigned view; see{' '}
            <Link to="/docs/guides/support-ai#handover">how handover works</Link>.
          </li>
          <li>A panel of similar past tickets on the ticket page.</li>
        </ul>
      </section>

      <section className="dp-section">
        <h2>Related</h2>
        <ul className="dp-connects">
          <li>
            <Link to="/docs/guides/support-knowledge">
              Build the knowledge base the AI answers from ›
            </Link>
          </li>
          <li>
            <Link to="/docs/guides/support-ai">How the AI first line works ›</Link>
          </li>
          <li>
            <Link to="/docs/guides/support-setup">Set up Support for your team ›</Link>
          </li>
        </ul>
      </section>
    </div>
  );
}
